import { Grid, useMediaQuery, useTheme } from '@material-ui/core'
import Paper from '@material-ui/core/Paper'
import { capitalize } from 'lodash'
import { Calendar } from 'lucide-react'
import moment from 'moment'
import {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState
} from 'react'
import { toast } from 'react-toastify'
import { ButtonAddNew } from '../../components/ButtonAddNew'
import ConfirmationModal from '../../components/ConfirmationModal'
import { InputSearch } from '../../components/InputSearch'
import MainContainer from '../../components/MainContainer'
import MainHeader from '../../components/MainHeader'
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper'
import QuickTable from '../../components/QuickTable'
import ScheduleModal from '../../components/ScheduleModal'
import { AuthContext } from '../../context/Auth/AuthContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { useStyles } from './style'

const getUrlParam = param => {
  return new URLSearchParams(window.location.search).get(param)
}

const reducer = (state, action) => {
  if (action.type === 'LOAD_SCHEDULES') {
    const schedules = action.payload
    const newSchedules = []

    schedules.forEach(schedule => {
      const scheduleIndex = state.findIndex(s => s.id === schedule.id)
      if (scheduleIndex !== -1) {
        state[scheduleIndex] = schedule
      } else {
        newSchedules.push(schedule)
      }
    })

    return [...state, ...newSchedules]
  }

  if (action.type === 'UPDATE_SCHEDULES') {
    const schedule = action.payload
    const scheduleIndex = state.findIndex(s => s.id === schedule.id)

    if (scheduleIndex !== -1) {
      state[scheduleIndex] = schedule
      return [...state]
    } else {
      return [schedule, ...state]
    }
  }

  if (action.type === 'DELETE_SCHEDULE') {
    const scheduleId = action.payload

    const scheduleIndex = state.findIndex(s => s.id === scheduleId)
    if (scheduleIndex !== -1) {
      state.splice(scheduleIndex, 1)
    }
    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }
}

const Schedules = () => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { user } = useContext(AuthContext)

  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [selectedSchedule, setSelectedSchedule] = useState(null)
  const [deletingSchedule, setDeletingSchedule] = useState(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [searchParam, setSearchParam] = useState('')
  const [schedules, dispatch] = useReducer(reducer, [])
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false)
  const [contactId, setContactId] = useState(+getUrlParam('contactId'))

  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get('/schedules/', {
        params: { searchParam, pageNumber }
      })
      dispatch({ type: 'LOAD_SCHEDULES', payload: data.schedules })
      setHasMore(data.hasMore)
      setLoading(false)
    } catch (err) {
      toastError(err)
    }
  }, [searchParam, pageNumber])

  const handleOpenScheduleModalFromContactId = useCallback(() => {
    if (contactId) {
      handleOpenScheduleModal()
    }
  }, [contactId])

  const socketManager = useContext(SocketContext)

  useEffect(() => {
    dispatch({ type: 'RESET' })
    setPageNumber(1)
  }, [searchParam])

  useEffect(() => {
    setLoading(true)
    const delayDebounceFn = setTimeout(() => {
      fetchSchedules()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [
    searchParam,
    pageNumber,
    contactId,
    fetchSchedules,
    handleOpenScheduleModalFromContactId
  ])

  useEffect(() => {
    handleOpenScheduleModalFromContactId()
    const socket = socketManager.GetSocket(user.companyId)

    const onSchedule = data => {
      if (data.action === 'update' || data.action === 'create') {
        dispatch({
          type: 'UPDATE_SCHEDULES',
          payload: data?.schedules || data?.schedule
        })
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_USER', payload: +data.scheduleId })
      }
    }

    socket.on(`company-${user.companyId}-schedule`, onSchedule)

    return () => {
      socket.disconnect()
    }
  }, [handleOpenScheduleModalFromContactId, user, socketManager])

  const handleCopySchedule = schedule => {
    const copiedSchedule = {
      ...schedule,
      id: null,
      body: schedule.body,
      contactId: schedule.contactId, // Mantém o ID do contato
      contact: schedule.contact,
      sendAt: '',
      sendTime: '',
      status: 'PENDENTE'
    }
    setSelectedSchedule(copiedSchedule)
    setScheduleModalOpen(true)
  }

  const cleanContact = () => {
    setContactId('')
  }

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null)
    setScheduleModalOpen(true)
  }

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null)
    setScheduleModalOpen(false)
  }

  const handleSearch = event => {
    setSearchParam(event.target.value.toLowerCase())
  }

  const handleEditSchedule = schedule => {
    setSelectedSchedule(schedule)
    setScheduleModalOpen(true)
  }

  const handleDeleteSchedule = async scheduleId => {
    try {
      await api.delete(`/schedules/${scheduleId}`)
      toast.success(i18n.t('schedules.toasts.deleted'))
    } catch (err) {
      toastError(err)
    }
    setDeletingSchedule(null)
    setSearchParam('')
    setPageNumber(1)

    dispatch({ type: 'RESET' })
    setPageNumber(1)
    await fetchSchedules()
  }

  const loadMore = () => {
    setPageNumber(prevState => prevState + 1)
  }

  const handleScroll = e => {
    if (!hasMore || loading) return
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore()
    }
  }

  const columns = [
    {
      field: 'contact.name',
      headerName: i18n.t('schedules.table.contact'),
      align: 'left'
    },
    {
      field: 'body',
      headerName: i18n.t('schedules.table.body'),
      align: 'left'
    },
    {
      field: 'sendAt',
      headerName: i18n.t('schedules.table.sendAt'),
      align: 'left'
    },
    {
      field: 'status',
      headerName: i18n.t('schedules.table.status'),
      align: 'left'
    }
  ]

  const customCellRenderer = {
    body: item => truncate(item.body, 25),
    sendAt: item => moment(item.sendAt).format('DD/MM/YYYY HH:mm:ss'),
    status: item => capitalize(item.status)
  }

  const truncate = (str, len) => {
    if (str.length > len) {
      return str.substring(0, len) + '...'
    }
    return str
  }

  return (
    <MainContainer>
      <MainHeader
        title={i18n.t('schedules.title')}
        description="Gerencie os agendamentos para enviar mensagens automáticas no
              momento certo e otimizar o atendimento."
        showTutorial={!isMobile}
        // onTutorialClick={handleOpenTutorial}
      >
        <MainHeaderButtonsWrapper>
          <ButtonAddNew
            variant="contained"
            onClick={handleOpenScheduleModal}
            startIcon={<Calendar size={20} />}
            label={isMobile ? 'Agendar' : 'Agendar Mensagem'}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Grid container className={classes.boxForms}>
        <Grid item xs={12} className={classes.selectContainer}>
          <InputSearch
            placeholder={i18n.t('contacts.searchPlaceholder')}
            type="search"
            name="searchSchedule"
            id="searchSchedule"
            value={searchParam}
            onChange={handleSearch}
          />
        </Grid>
      </Grid>
      <ConfirmationModal
        title={
          deletingSchedule &&
          `${i18n.t('schedules.confirmationModal.deleteTitle')}`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        {i18n.t('schedules.confirmationModal.deleteMessage')}
      </ConfirmationModal>
      <ScheduleModal
        open={scheduleModalOpen}
        onClose={handleCloseScheduleModal}
        reload={fetchSchedules}
        aria-labelledby="form-dialog-title"
        scheduleId={selectedSchedule?.id || undefined}
        contactId={selectedSchedule?.contactId || contactId}
        cleanContact={cleanContact}
        initialValues={selectedSchedule?.id === null ? selectedSchedule : null}
      />
      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        <QuickTable
          data={schedules}
          columns={columns}
          loading={loading}
          actions={row => {
            const isSent = row.status?.toLowerCase() === 'enviada'
            return ['copy', ...(!isSent ? ['edit'] : []), 'delete']
          }}
          onEdit={handleEditSchedule}
          onDelete={schedule => {
            setConfirmModalOpen(true)
            setDeletingSchedule(schedule)
          }}
          onCopy={handleCopySchedule}
          customCellRenderer={customCellRenderer}
          emptyMessage="Nenhum agendamento encontrado."
          showAlternatingRows={true}
          containerProps={{ elevation: 0 }}
        />
      </Paper>
    </MainContainer>
  )
}

export default Schedules
